/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';
import type { Interpolation, Theme } from '@emotion/react';

import * as React from 'react';

import { InlineNotification } from '@coursera/cds-core';
import type { InlineNotificationProps } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import { type SsoBannerName } from 'bundles/authentication/constants';

type SSOEnforcementBannerProps = {
  bannerName: SsoBannerName;
  children: InlineNotificationProps['children'];
  css?: Interpolation<Theme>;
};

/**
 * SSO Enforcement Banner Component with built-in visibility tracking.
 *
 * This component automatically handles visibility tracking for SSO error banners
 * when they appear in the DOM, eliminating timing issues with conditional rendering.
 *
 * @param bannerName - Name of the SSO banner for tracking purposes
 * @param children - Banner content to display
 * @param css - Custom CSS styles
 */
const SSOEnforcementBanner = ({ bannerName, children, css: customCss, ...props }: SSOEnforcementBannerProps) => {
  const ref = useVisibilityTracker('view_banner', {
    bannerName,
  });

  return (
    <InlineNotification severity="error" css={customCss} ref={ref as React.RefObject<HTMLDivElement>} {...props}>
      {children}
    </InlineNotification>
  );
};

export default SSOEnforcementBanner;
